import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Activity, PublicAmorgUser, Survey, SurveyReport, UserToken } from '../../../amorg-common';
import { getActivity } from '../../../api/activityClientApi';
import { getAuthorizedActiveUserOrRedirect } from '../../../api/serverApiHelpers';
import { getSurveyByActivityId, getSurveyReport } from '../../../api/surveyClientApi';
import { getUsers } from '../../../api/userClientApi';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { P } from '../../../designSystem/typography/Body';
import { clientAccessHelper } from '../../../utils/clientAccessHelper';
import { ProtectedPageContext } from '../../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../../_pageWrappers/MemberPageWrapper';
import { WorkplaceAssessmentComponentProps } from './_components/tabs/_components/WorkplaceAssessmentComponentProps';
import { ActionPlansPanel } from './_components/tabs/actionplan/ActionPlansPanel';
import AnswersPanel from './_components/tabs/answer/AnswersPanel';
import { OverviewPanel } from './_components/tabs/overview/OverviewPanel';
import { ReportPanel } from './_components/tabs/report/ReportPanel';
import { SurveyPanel } from './_components/tabs/survey/SurveyPanel';
import { PageTopBarBreadcrumb } from '../../_pageWrappers/wrapperLayouts/elements/topBar/TopBar';
import { Tabber2 } from '../../../designSystem/Tabber2';
import { colors } from '../../../designSystem/colors/color';


export default function WorkplaceAssessmentPage(): JSX.Element {
    
    const [breadCrumbs, setBreadCrumbs] = React.useState<PageTopBarBreadcrumb[] | undefined>(undefined);

    return (
        <MemberPageWrapper
            selectedMenuItemKey="workplaceassessment"
            breadCrumbs={breadCrumbs}>

            <WorkplaceAssessmentPageContent
                onSetBreadcrumbs={setBreadCrumbs} />

        </MemberPageWrapper>
    );
}

type WorkplaceAssessmentPageContentProps = {
    onSetBreadcrumbs:(breadcrumbs: PageTopBarBreadcrumb[] | undefined) => void
}

function WorkplaceAssessmentPageContent(props:WorkplaceAssessmentPageContentProps): JSX.Element
{
    const params = useParams<{ activityId: string }>();
    const activeUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(activeUser, ['ViewActivity']);

    if (!hasAccess)
        return <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>;

    return params.activityId 
        ? <WorkPlaceAssessmentPageView
            activityId={params.activityId}
            onSetBreadcrumbs={props.onSetBreadcrumbs} />
        : <span>ugyldig url</span>;
}


type State ={
    loading: boolean,
    activeUser: UserToken | null,
    users: PublicAmorgUser[],
    activity:Activity | null,
    survey: Survey | null,
    surveyReport: SurveyReport | null,
}

type WorkPlaceAssessmentPageViewProps =
{
    activityId:string,
    onSetBreadcrumbs:(breadcrumbs: PageTopBarBreadcrumb[] | undefined) => void
}

function WorkPlaceAssessmentPageView(props:WorkPlaceAssessmentPageViewProps): JSX.Element {
    
    const [state, setState] = React.useState<State>(
        {
            loading: true,
            activeUser: null,
            activity: null,
            users: [],
            survey: null,
            surveyReport: null
        }
    );

    const [selectedTab, setSelectedTab] = React.useState('overview');
    const [activity, setActivity] = React.useState<{id:string, title:string} | null>(null);
    const [selectedActionPlan, setSelectedActionPlan] = React.useState<{id:string, title:string} | null>(null);

    const selectedTabFromUrl = new URLSearchParams(window.location.search).get('tab') ?? '';
    const tab = ['overview','survey','answers','actionplans','report'].includes(selectedTabFromUrl) ? selectedTabFromUrl : 'overview';

    React.useEffect(function(){
        setSelectedTab(tab);
        setSelectedActionPlan(null);
    }, [tab]);

    React.useEffect(() => {
        props.onSetBreadcrumbs(buildBreadcrumbs());
    }, [activity, selectedTab, selectedActionPlan]);

    React.useEffect(function () {
        updateState();
    }, [props.activityId]);

    function buildBreadcrumbs():PageTopBarBreadcrumb[] {
        const breadCrumbs=[
            {text:"APV'er", url:'/workplaceassessments'},
            ...(activity ? [{text:activity.title, url:`/workplaceassessments/${activity.id}`}] : []),
            ...(selectedTab === 'overview' ? [{text:'Oversigt', url:''}] : []),
            ...(selectedTab === 'survey' ? [{text:'Spørgeskema', url:''}] : []),
            ...(selectedTab === 'answers' ? [{text:'Besvarelser', url:''}] : []),
            ...(selectedTab === 'report' ? [{text:'Rapport', url:''}] : []),
            ...(selectedTab === 'actionplans' ? [{text:'Handleplaner', 
                url:selectedActionPlan ? `/workplaceassessments/${activity?.id}?tab=actionplans` : ''}] : []
            ),
            ...(selectedActionPlan ? [{text:selectedActionPlan.title, url:''}] : [])
        ];
        return breadCrumbs;
    }


    async function updateState() {
        setState({...state, loading:true});
        const activeUser = await getAuthorizedActiveUserOrRedirect(['ViewActivity']);
        const activity = await getActivity(props.activityId);
        if(!activity)
            return;

        setActivity({ title: activity.title, id: activity.id });

        const users = await getUsers();
        const survey = await getSurveyByActivityId(activity.id);
        if (survey !== null) {
            const surveyReport = await getSurveyReport(survey.id);
            setState({
                loading:false,
                activeUser,
                activity,
                users,
                survey,
                surveyReport
            });
        }
        else {
            setState({
                loading:false,
                activeUser,
                activity,
                users,
                survey: null,
                surveyReport: null
            });
        }
    }



    const componentProps:WorkplaceAssessmentComponentProps | null = 
        state.activity && state.activeUser
        ? {
                activeUser:state.activeUser,
                users:state.users,
                activity:state.activity,
                surveyLoading:state.loading,
                survey:state.survey,
                surveyReport:state.surveyReport,
                onActivityUpdated:() => {
                    updateState();
                },
                onSurveyUpdated:() => updateState(),
            }
        : null;

    return (
        <>
            <LoadingPageOverlay loading={state.loading} />

            {state.activity &&
                <>
                    <div style={{borderBottom:`1px solid ${colors.divider.light}`, borderRadius: '4px', backgroundColor:'white'}}>
                        <Tabber2 variant='text' alignment='center'
                            items={[
                                { tabName:'overview', text:'Oversigt'},
                                { tabName:'survey', text:'Spørgeskema', disabled:state.survey===null},
                                { tabName:
                                    'answers', 
                                    text:'Besvarelser',
                                    avatarCount: state.survey ? state.survey.finishedAnswerCount : 0,
                                    disabled:state.survey===null
                                },
                                { tabName:'report', text:'Rapport', disabled:state.survey===null||state.survey.finishedAnswerCount===0},
                                { tabName:'actionplans', text:'Handleplaner'},
                            ]}
                            selectedTabName={selectedTab}
                            onClick={(tabName) => {
                                window.history.replaceState(null, '', `/workplaceassessments/${props.activityId}?tab=${tabName}`);
                                setSelectedTab(tabName);
                            }}
                        />
                    </div>

                    {componentProps &&
                        <>
                            {selectedTab === 'overview' &&
                                <OverviewPanel
                                    {...componentProps} />
                            }

                            {selectedTab === 'survey' && state.survey &&
                                <SurveyPanel {...componentProps} />
                            }

                            {selectedTab === 'answers' && state.survey &&
                                <AnswersPanel {...componentProps} survey={state.survey} />
                            }

                            {selectedTab === 'report' && state.survey && state.surveyReport &&
                                <ReportPanel  {...componentProps} survey={state.survey} surveyReport={state.surveyReport} />
                            }

                            {selectedTab === 'actionplans' &&
                                <ActionPlansPanel  {...componentProps} onPlanLoaded={(id,title) => {
                                    setSelectedActionPlan(id ? {id, title} : null);
                                }} />
                            }
                        </>
                    }
                </>
            }
        </>
    );
}